import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOHeadProps {
  title: string;
  titleTh?: string;
  description: string;
  descriptionTh?: string;
  ogDescription?: string;
  twitterDescription?: string;
  path: string; // path without /en prefix (e.g., "/about-us", "/blog")
  ogImage?: string;
  ogType?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string;
  noIndex?: boolean;
  structuredData?: object | object[];
}

const SITE_URL = "https://carbonwize.io";

/**
 * Centralized SEO component with proper hreflang and canonical tags.
 *
 * Structured-data JSON-LD scripts are rendered as plain JSX (not via
 * react-helmet-async) so they are reliably serialized into the
 * prerendered static HTML. Helmet's script-tag injection sometimes lands
 * after the prerender snapshot, which makes Google Rich Results / AI
 * crawlers miss the schema entirely.
 */
const SEOHead = ({
  title,
  titleTh,
  description,
  descriptionTh,
  ogDescription,
  twitterDescription,
  path,
  ogImage = "/og-thumbnail.png",
  ogType = "website",
  publishedTime,
  modifiedTime,
  author = "Carbonwize",
  keywords = "ความยั่งยืน, คาร์บอน, คาร์บอนฟุตพริ้นท์, คาร์บอนเครดิต, CFO Platform, carbon accounting, sustainability, carbon footprint, carbon credit, ESG, TGO, ISO 14064",
  noIndex = false,
  structuredData,
}: SEOHeadProps) => {
  const { language } = useLanguage();

  // Clean path - ensure it starts with / and doesn't have /en prefix
  const cleanPath = path.replace(/^\/en/, "").replace(/^([^/])/, "/$1") || "/";

  // Get canonical URL based on language
  const canonicalUrl =
    language === "en" ? `${SITE_URL}/en${cleanPath === "/" ? "" : cleanPath}` : `${SITE_URL}${cleanPath}`;

  // Thai URL (canonical for Thai content, used in hreflang)
  const thUrl = `${SITE_URL}${cleanPath}`;

  // English URL (used in hreflang)
  const enUrl = `${SITE_URL}/en${cleanPath === "/" ? "" : cleanPath}`;

  // Select content based on language
  const currentTitle = language === "th" && titleTh ? titleTh : title;
  const currentDescription = language === "th" && descriptionTh ? descriptionTh : description;

  // Full og image URL
  const ogImageUrl = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  // Build structured data array
  const structuredDataArray = Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : [];

  // Markdown alternate — `.md` companion file emitted alongside each
  // prerendered HTML page (see scripts/generate-markdown.ts). Surfaces a
  // clean text/markdown view of the page for AI crawlers (ChatGPT,
  // Claude, Perplexity, Google AI) without needing server middleware.
  const mdUrl =
    language === "en"
      ? `${SITE_URL}/en${cleanPath === "/" ? "" : cleanPath}.md`
      : `${SITE_URL}${cleanPath === "/" ? "/index.md" : `${cleanPath}.md`}`;

  return (
    <>
      <Helmet>
        {/* Basic Meta */}
        <html lang={language} />
        <title>{currentTitle}</title>
        <meta name="description" content={currentDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content={author} />

        {/* Canonical and hreflang */}
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="th" href={thUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={thUrl} />

        {/* Markdown alternate for AI crawlers */}
        <link rel="alternate" type="text/markdown" href={mdUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={currentTitle} />
        <meta property="og:description" content={ogDescription ?? currentDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content={ogType} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="Carbonwize" />
        <meta property="og:locale" content={language === "th" ? "th_TH" : "en_US"} />
        <meta property="og:locale:alternate" content={language === "th" ? "en_US" : "th_TH"} />

        {/* Article specific */}
        {ogType === "article" && publishedTime && <meta property="article:published_time" content={publishedTime} />}
        {ogType === "article" && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
        {ogType === "article" && author && <meta property="article:author" content={author} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentTitle} />
        <meta name="twitter:description" content={twitterDescription ?? currentDescription} />
        <meta name="twitter:image" content={ogImageUrl} />

        {/* Robots */}
        {noIndex && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>

      {/* Structured Data — rendered as JSX (not via Helmet) so the scripts
          are reliably serialized into the prerendered static HTML, where
          Google Rich Results and AI crawlers will pick them up without JS. */}
      {structuredDataArray.map((data, index) => (
        <script
          key={`ld-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </>
  );
};

export default SEOHead;
